import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ShieldCheck, AlertTriangle, Check } from "lucide-react";

/* Human-readable copy for every permission surfaced by the backend. */
const PERMISSION_LABELS = {
  "dashboard.view": "View dashboards & reports",
  "branch.manage": "Manage branches",
  "user.manage": "Manage users & roles",
  "product.manage": "Manage catalog & products",
  "order.view": "View orders",
  "order.create": "Create orders",
  "order.modify": "Modify orders",
  "order.void": "Void orders",
  "payment.process": "Process payments",
  "refund.request": "Request refunds",
  "refund.approve": "Approve refunds",
  "discount.apply": "Apply discounts",
  "discount.approve": "Approve discounts",
  "inventory.view": "View inventory",
  "inventory.adjust": "Adjust inventory",
  "reservation.manage": "Manage reservations",
  "reservation.open_pos_tab": "Open POS tab from reservation",
  "event.manage": "Manage events",
  "guest_list.manage": "Manage guest lists",
  "entrance.scan": "Scan entrance QR",
  "entrance.override": "Override entrance rules",
  "entrance.session.manage": "Manage entrance sessions",
  "id_verification.perform": "Verify guest IDs",
  "foot_traffic.view": "View foot traffic",
  "attendance.manage": "Manage attendance",
  "attendance.own": "Log own attendance",
  "payroll.view": "View payroll",
  "payroll.prepare": "Prepare pay runs",
  "payslip.view_own": "View own payslip",
  "cash_session.manage": "Manage cash sessions",
  "approval.manage": "Manage approvals",
  "settings.manage": "Manage settings",
  "audit_log.view": "View audit log",
  "kds.view": "View kitchen display",
  "kds.update": "Update kitchen tickets",
  "table.manage": "Manage tables & floors",
  "table.status_update": "Update table status",
  "promoter.commission.view": "View promoter commissions",
  "promoter.commission.manage": "Manage promoter commissions",
  "promoter.payout.view": "View promoter payouts",
  "promoter.payout.manage": "Manage promoter payouts",
};

const PERMISSION_CATEGORIES = [
  { key: "pos", label: "POS & Orders", match: /^(order|payment|refund|discount|kds|table)\./ },
  { key: "reservations", label: "Reservations & Guests", match: /^(reservation|guest|guest_list|foot_traffic)\./ },
  { key: "events", label: "Events & Promoters", match: /^(event|promoter)\./ },
  { key: "inventory", label: "Inventory & Purchasing", match: /^(inventory|purchasing|supplier|product)\./ },
  { key: "entrance", label: "Entrance & Security", match: /^(entrance|id_verification|facility)\./ },
  { key: "finance", label: "Finance & Cash", match: /^(cash_session|approval)\./ },
  { key: "payroll", label: "Employees & Payroll", match: /^(attendance|payroll|payslip)\./ },
  { key: "marketing", label: "Marketing", match: /^marketing\./ },
  { key: "operations", label: "Operations", match: /^operations\./ },
  { key: "users", label: "User & Access Management", match: /^(user|branch)\./ },
  { key: "settings", label: "Settings & Audit", match: /^(settings|audit_log|dashboard)\./ },
];

const SENSITIVE_PERMS = new Set([
  "user.manage", "branch.manage", "settings.manage", "order.void",
  "refund.approve", "discount.approve", "approval.manage",
  "audit_log.view", "promoter.payout.manage",
]);

const ROLE_DESCRIPTIONS = {
  business_owner: "Full control of the organization, including finance and access.",
  general_manager: "Cross-branch operator with broad management authority.",
  branch_manager: "Manages daily branch operations, staff, and reports.",
  cashier: "Processes orders and payments through the POS.",
  server: "Takes orders and updates tables from the floor.",
  bartender: "Fulfills drink tickets from the bar station.",
  kitchen_staff: "Fulfills food tickets from the kitchen display.",
  inventory_controller: "Manages stock, adjustments, and purchasing lanes.",
  host: "Runs check-in queue, seating, and VIP notes.",
  event_manager: "Runs events, promoters, and guest-list operations.",
  promoter: "Sells tickets and manages personal guest lists.",
  security_personnel: "Runs the door — scan, ID checks, incident intake.",
  hr_payroll_officer: "Manages employees, attendance, and payroll runs.",
  employee: "Self-service — attendance and own payslip only.",
  read_only_viewer: "Read-only access to dashboards and reports.",
};

function permLabel(p) {
  return PERMISSION_LABELS[p] || p.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function RolesMatrixPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/roles").then(({ data }) => {
      setRoles(data);
      setSelected(data[0]?.key || null);
    }).catch(() => toast.error("Failed to load roles")).finally(() => setLoading(false));
  }, []);

  const active = roles.find((r) => r.key === selected);

  const filteredPerms = useMemo(() => {
    if (!active) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return active.permissions;
    return active.permissions.filter((p) =>
      p.toLowerCase().includes(needle) || permLabel(p).toLowerCase().includes(needle));
  }, [active, q]);

  return (
    <div className="h-full w-full overflow-y-auto" data-testid="roles-matrix-page">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-6 space-y-5">
        <div>
          <div className="section-eyebrow flex items-center gap-2">
            <span className="section-num">.16</span>
            <span className="h-px w-6 bg-foreground/25" />
            Team & Access
          </div>
          <h1 className="mt-2 display-heading text-3xl md:text-4xl ink leading-none tracking-tight">
            Roles &amp; permissions
          </h1>
          <p className="mt-2 text-[12.5px] ink-muted">
            System roles are read-only.  Each role bundles a fixed set of permissions grouped by business area.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-4">
          {/* Role list */}
          <div className="rounded-lg border hairline surface-card p-2 h-fit max-h-[75vh] overflow-y-auto">
            {loading && <div className="p-4 text-[11.5px] ink-muted">Loading…</div>}
            {roles.map((r) => (
              <button key={r.key} onClick={() => setSelected(r.key)}
                data-testid={`role-item-${r.key}`}
                className={`w-full text-left px-3 py-2.5 rounded-md text-[12.5px] transition-colors ${
                  selected === r.key ? "bg-primary/10 ink" : "ink hover:bg-foreground/[0.05]"
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{r.label}</span>
                  <span className="text-[10px] ink-subtle mono shrink-0">{r.permissions.length}</span>
                </div>
                <div className="text-[10.5px] ink-subtle truncate mt-0.5">
                  {ROLE_DESCRIPTIONS[r.key] || `${r.permissions.length} permissions`}
                </div>
              </button>
            ))}
          </div>

          {/* Role detail */}
          {active && (
            <div className="rounded-lg border hairline surface-card p-5 space-y-4" data-testid={`role-detail-${active.key}`}>
              <div className="flex items-start justify-between gap-3 pb-4 border-b hairline-soft">
                <div className="min-w-0">
                  <div className="tiny-label">Role</div>
                  <h2 className="mt-1 text-xl font-bold ink tracking-tight">{active.label}</h2>
                  <p className="mt-1 text-[12px] ink-muted max-w-[520px]">
                    {ROLE_DESCRIPTIONS[active.key] || "Predefined system role."}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <div className="inline-flex items-center gap-1.5 h-6 px-2 rounded-md border hairline bg-foreground/[0.04] text-[10.5px] ink-muted">
                    <ShieldCheck size={11}/> System role · Read-only
                  </div>
                  <div className="mt-1.5 text-[11px] ink-subtle">
                    {active.permissions.length} permissions
                  </div>
                </div>
              </div>

              <div className="relative max-w-[320px]">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 ink-subtle" />
                <input
                  value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search permissions…"
                  data-testid="role-permission-search"
                  className="h-9 w-full rounded-lg bg-foreground/[0.03] border hairline pl-8 pr-3 text-[12.5px] ink placeholder:ink-subtle focus:outline-none focus:border-foreground/30"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PERMISSION_CATEGORIES.map((cat) => {
                  const perms = filteredPerms.filter((p) => cat.match.test(p));
                  if (perms.length === 0) return null;
                  return (
                    <div key={cat.key} data-testid={`role-cat-${cat.key}`}
                      className="rounded-md border hairline-soft bg-foreground/[0.02] p-3">
                      <div className="tiny-label mb-2 flex items-center justify-between">
                        <span>{cat.label}</span>
                        <span className="mono ink-subtle text-[10px]">{perms.length}</span>
                      </div>
                      <ul className="space-y-1">
                        {perms.map((p) => (
                          <li key={p} className="flex items-start gap-2 text-[11.5px] ink">
                            <Check size={11} className="mt-0.5 text-emerald-400 shrink-0" />
                            <span className="flex-1">{permLabel(p)}</span>
                            {SENSITIVE_PERMS.has(p) && (
                              <span title="Sensitive permission" className="shrink-0">
                                <AlertTriangle size={10} className="text-amber-400"/>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
                {/* Uncategorized fallback */}
                {(() => {
                  const other = filteredPerms.filter((p) =>
                    !PERMISSION_CATEGORIES.some((c) => c.match.test(p)));
                  if (other.length === 0) return null;
                  return (
                    <div className="rounded-md border hairline-soft bg-foreground/[0.02] p-3">
                      <div className="tiny-label mb-2">Other</div>
                      <ul className="space-y-1">
                        {other.map((p) => (
                          <li key={p} className="flex items-start gap-2 text-[11.5px] ink">
                            <Check size={11} className="mt-0.5 text-emerald-400 shrink-0" />
                            <span>{permLabel(p)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })()}
              </div>

              {filteredPerms.length === 0 && (
                <div className="text-center py-8 ink-muted text-[12px]">
                  No permissions match "{q}".
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
